/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 * Değişiklik gerekiyorsa Core'da güncellenir ve senkron ile diğer repolara yayılır.
 */

import {
  GUIDED_LEARNING_TARGET_ATTRIBUTE,
  guidedLearningTargetSelector,
  type GuidedLearningRuntimeCapability,
  type GuidedLearningSemanticEvent,
} from './guided-learning.contract';

export interface GuidedLearningTargetCommand {
  capability: GuidedLearningRuntimeCapability;
  value?: string;
  optionKey?: string;
}

export interface GuidedLearningTargetRegistration {
  key: string;
  capabilities: GuidedLearningRuntimeCapability[];
  resolveElement?: () => HTMLElement | null;
  execute?: (command: GuidedLearningTargetCommand) => void | Promise<void>;
}

export interface GuidedLearningRouteRegistration {
  key: string;
  navigate: () => void | Promise<void>;
}

const targetRegistrations = new Map<string, GuidedLearningTargetRegistration>();
const routeRegistrations = new Map<string, GuidedLearningRouteRegistration>();

function visibleElement(element: HTMLElement | null): element is HTMLElement {
  if (!element || !element.isConnected) return false;
  const style = window.getComputedStyle(element);
  const rect = element.getBoundingClientRect();
  return style.display !== 'none' && style.visibility !== 'hidden' && rect.width > 0 && rect.height > 0;
}

function resolveDomTarget(key: string): HTMLElement | null {
  const selector = guidedLearningTargetSelector(key);
  if (!selector) return null;
  return Array.from(document.querySelectorAll<HTMLElement>(selector)).find(visibleElement) ?? null;
}

function setNativeValue(element: HTMLInputElement | HTMLTextAreaElement, value: string): void {
  if (element instanceof HTMLInputElement && (element.type === 'password' || element.type === 'file')) {
    throw new Error('GUIDED_LEARNING_SENSITIVE_TARGET');
  }
  const prototype = element instanceof HTMLTextAreaElement
    ? HTMLTextAreaElement.prototype
    : HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(prototype, 'value')?.set;
  if (!setter) throw new Error('GUIDED_LEARNING_TARGET_NOT_SETTABLE');
  setter.call(element, value);
  element.dispatchEvent(new Event('input', { bubbles: true }));
  element.dispatchEvent(new Event('change', { bubbles: true }));
}

function findSemanticOption(element: HTMLElement, optionSelector: string): HTMLElement | null {
  const targetKey = element.getAttribute(GUIDED_LEARNING_TARGET_ATTRIBUTE);
  return element.querySelector<HTMLElement>(optionSelector)
    ?? (targetKey
      ? document.querySelector<HTMLElement>(
        `[${GUIDED_LEARNING_TARGET_ATTRIBUTE}="${targetKey}"]${optionSelector}`,
      )
      : null);
}

async function waitForSemanticOption(
  element: HTMLElement,
  optionSelector: string,
): Promise<HTMLElement | null> {
  let option = findSemanticOption(element, optionSelector);
  if (option || !element.matches('[role="combobox"]')) return option;

  // Portal tabanlı ortak select, seçeneklerini ancak trigger açıldıktan sonra DOM'a ekler.
  element.click();
  for (let attempt = 0; attempt < 8; attempt += 1) {
    await new Promise<void>((resolve) => {
      if (typeof requestAnimationFrame === 'function') requestAnimationFrame(() => resolve());
      else setTimeout(resolve, 0);
    });
    option = findSemanticOption(element, optionSelector);
    if (option) return option;
  }
  return null;
}

async function executeDomCommand(
  element: HTMLElement,
  command: GuidedLearningTargetCommand,
): Promise<void> {
  if (command.capability === 'FOCUS') {
    const focusTarget = element.matches('input, textarea, select, button, [tabindex]')
      ? element
      : element.querySelector<HTMLElement>('input, textarea, select, button, [tabindex]');
    focusTarget?.focus();
    return;
  }
  if (command.capability === 'ACTIVATE') {
    const activationTarget = element.matches('button, [role="button"], [role="tab"], a')
      ? element
      : element.querySelector<HTMLElement>('button, [role="button"], [role="tab"], a');
    if (!activationTarget) throw new Error('GUIDED_LEARNING_TARGET_NOT_ACTIVATABLE');
    activationTarget.click();
    return;
  }
  if (command.capability === 'SET_VALUE') {
    const input = element.matches('input, textarea')
      ? element
      : element.querySelector<HTMLElement>('input, textarea');
    if (!(input instanceof HTMLInputElement) && !(input instanceof HTMLTextAreaElement)) {
      throw new Error('GUIDED_LEARNING_TARGET_NOT_SETTABLE');
    }
    setNativeValue(input, command.value ?? '');
    return;
  }
  if (command.capability === 'SELECT') {
    const optionKey = command.optionKey ?? '';
    if (!optionKey) throw new Error('GUIDED_LEARNING_OPTION_REQUIRED');
    const optionSelector = `[data-qp-learning-option-key="${optionKey}"]`;
    const option = await waitForSemanticOption(element, optionSelector);
    if (!option) throw new Error('GUIDED_LEARNING_OPTION_NOT_FOUND');
    if (option instanceof HTMLOptionElement && option.parentElement instanceof HTMLSelectElement) {
      const select = option.parentElement;
      const setter = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, 'value')?.set;
      if (!setter) throw new Error('GUIDED_LEARNING_TARGET_NOT_SELECTABLE');
      setter.call(select, option.value);
      select.dispatchEvent(new Event('input', { bubbles: true }));
      select.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }
    option.click();
  }
}

export function registerGuidedLearningTarget(
  registration: GuidedLearningTargetRegistration,
): () => void {
  if (!guidedLearningTargetSelector(registration.key) || registration.capabilities.length === 0) {
    throw new Error('Invalid Guided Learning target registration.');
  }
  targetRegistrations.set(registration.key, registration);
  return () => {
    if (targetRegistrations.get(registration.key) === registration) {
      targetRegistrations.delete(registration.key);
    }
  };
}

export function registerGuidedLearningRoute(
  registration: GuidedLearningRouteRegistration,
): () => void {
  if (!guidedLearningTargetSelector(registration.key)) {
    throw new Error('Invalid Guided Learning route registration.');
  }
  routeRegistrations.set(registration.key, registration);
  return () => {
    if (routeRegistrations.get(registration.key) === registration) {
      routeRegistrations.delete(registration.key);
    }
  };
}

export async function executeGuidedLearningTarget(
  key: string,
  command: GuidedLearningTargetCommand,
): Promise<HTMLElement> {
  const registration = targetRegistrations.get(key);
  if (registration && !registration.capabilities.includes(command.capability)) {
    throw new Error('GUIDED_LEARNING_CAPABILITY_NOT_ALLOWED');
  }
  const element = registration?.resolveElement?.() ?? resolveDomTarget(key);
  if (!visibleElement(element)) throw new Error('GUIDED_LEARNING_TARGET_UNAVAILABLE');
  if (registration?.execute) await registration.execute(command);
  else await executeDomCommand(element, command);
  return element;
}

export async function executeGuidedLearningRoute(key: string): Promise<void> {
  const registration = routeRegistrations.get(key);
  if (!registration) throw new Error('GUIDED_LEARNING_ROUTE_UNAVAILABLE');
  await registration.navigate();
}

export function executeGuidedLearningRoutePath(path: string): void {
  if (!/^\/[a-z0-9][a-z0-9/_-]{0,239}$/.test(path) || path.includes('..')) {
    throw new Error('GUIDED_LEARNING_ROUTE_PATH_INVALID');
  }
  if (window.location.pathname === path) return;
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

function resolveCapability(event: Event, target: HTMLElement): GuidedLearningRuntimeCapability | null {
  if (event.type === 'focusin') return 'FOCUS';
  if (event.type === 'input') return 'SET_VALUE';
  if (event.type === 'change') {
    return target.matches('select, [role="combobox"]') || target.closest('[role="combobox"]')
      ? 'SELECT'
      : 'SET_VALUE';
  }
  if (event.type === 'click') {
    return target.hasAttribute('data-qp-learning-option-key') ? 'SELECT' : 'ACTIVATE';
  }
  return null;
}

export function subscribeGuidedLearningSemanticEvents(
  listener: (event: GuidedLearningSemanticEvent) => void,
): () => void {
  if (typeof document === 'undefined') return () => undefined;
  const handleEvent = (event: Event) => {
    if (!(event.target instanceof HTMLElement)) return;
    const target = event.target.closest<HTMLElement>(`[${GUIDED_LEARNING_TARGET_ATTRIBUTE}]`);
    if (!target) return;
    const targetKey = target.getAttribute(GUIDED_LEARNING_TARGET_ATTRIBUTE) ?? '';
    if (!guidedLearningTargetSelector(targetKey)) return;
    const capability = resolveCapability(event, event.target);
    if (!capability) return;
    listener({
      targetKey,
      capability,
      ...(target.getAttribute('data-qp-learning-option-key')
        ? { optionKey: target.getAttribute('data-qp-learning-option-key')! }
        : {}),
      occurredAt: new Date().toISOString(),
    });
  };
  const eventNames = ['focusin', 'input', 'change', 'click'] as const;
  eventNames.forEach((name) => document.addEventListener(name, handleEvent, true));
  return () => eventNames.forEach((name) => document.removeEventListener(name, handleEvent, true));
}

export function clearGuidedLearningRegistriesForTests(): void {
  targetRegistrations.clear();
  routeRegistrations.clear();
}
